import { Fragment, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectUserId } from '~/auth/slices/auth.slice'
import { useGetBootcampActivityDataQuery } from '~/bootcamp/apis/get-activity.api'

import { selectBootcampPageLevelIds, setBootcampLevelProgress } from '~/bootcamp/slices/bootcamp-page.slice'
import { selectVideoTopicColor, selectVccSubjectName, selectVccPageTitle } from '~/bootcamp/slices/vcc-data.slice'

import { BootcampPageUrlParamsType, LevelActionType } from '~/bootcamp/types/bootcamp.types'

import ModalChestsCounter from '~/bootcamp/pages/BootcampView/BootcampContent/ModalChestsCounter'

import { arrayContainsAll, Breakpoint, useCheckBreakpoint } from '~/utils'

import PlayerHealth from '~/bootcamp/components/Quiz/PlayerHealthBar'

import {
  selectBossBattleEnemy,
  selectBossIsBusy,
  selectHasStartedQuiz,
  selectQuizState,
} from '~/bootcamp/slices/bootcamp-quiz.slice'

import BossSplashScreen from '~/bootcamp/components/Quiz/BossSplashScreen'

import { QuizState } from '~/bootcamp/components/Quiz/QuizData.models'

import { BossBattleEnemy } from '~/bootcamp/components/Quiz/QuizProps.models'

import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'

import useShowFunFactChest from '~/bootcamp/hooks/useShowFunFactChest'
import BossSoundControl from '~/bootcamp/components/Quiz/BossSoundControl'

import { selectIsLastLevel, selectLastLevelGradeInfo, selectLastLevelIds } from '~/curriculum/slice/curriculum.slice'
import { useClaimCertificate } from '~/curriculum/hooks/useClaimCertificate'

import QuizBoss from './QuizBoss'
import { FunFactChest } from './FunFactChest'
import ContentSection from './ContentSection'

const BootcampContent = ({ isVideoWatched }: { isVideoWatched: boolean }): React.ReactElement => {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const { query, locale } = router
  const isShowGrade = router.query?.showGrade === 'true'
  const isActivity = query?.action === LevelActionType.Activity
  const { action, key, level, moduleKey } = query as unknown as BootcampPageUrlParamsType

  const userId = useAppSelector(selectUserId)

  const { data: levelProgress } = useGetBootcampActivityDataQuery(
    { key, moduleKey, level, userId, language: locale ?? 'en' },
    { skip: !userId },
  )

  const isQuizPage = query?.action === LevelActionType.Quiz
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })

  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const quizState = useAppSelector<QuizState>(selectQuizState)
  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)
  const bossBattleEnemy = useAppSelector<BossBattleEnemy>(selectBossBattleEnemy)
  const showBossAnimation = useAppSelector(selectBossIsBusy)

  const subjectName = useAppSelector(selectVccSubjectName)
  const title = useAppSelector(selectVccPageTitle)
  const hasStartedQuiz = useAppSelector(selectHasStartedQuiz)
  const pageLevelIds = useAppSelector(selectBootcampPageLevelIds)
  const isLastLevel = useAppSelector(selectIsLastLevel)
  const lastLevelIds = useAppSelector(selectLastLevelIds)
  const lastLevelGradeInfo = useAppSelector(selectLastLevelGradeInfo)
  const showFunFactChest = useShowFunFactChest()
  const isSummeryScreen =
    quizState.totalQuestions === quizState.completedQuestions || quizState.wrongAnswer > quizState.maxWrongAnswer
  const { claimCertificate } = useClaimCertificate()

  useEffect(() => {
    const generateCertificate = async (): Promise<void> => {
      const completedIds = levelProgress?.completedIds || []
      const isLevelCompleted =
        pageLevelIds && pageLevelIds?.length > 0 ? arrayContainsAll(completedIds, pageLevelIds) : false
      const shouldClaimCertificate =
        arrayContainsAll(pageLevelIds || [], lastLevelIds) && isLastLevel && isLevelCompleted
      if (shouldClaimCertificate) {
        await claimCertificate(lastLevelGradeInfo)
      }
    }
    generateCertificate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastLevel, levelProgress?.completedIds, pageLevelIds])

  useEffect(() => {
    dispatch(
      setBootcampLevelProgress({
        collectedRewards: levelProgress?.collectedRewards,
        processedLeafs: levelProgress?.processedLeafs,
        level,
        action,
        isVideoWatched,
      }),
    )
  }, [levelProgress, dispatch, level, action, isVideoWatched])

  const funFactChest = (
    <div className="mt-5 md:mt-6">
      <FunFactChest />
    </div>
  )

  return (
    <Fragment>
      <article
        className={`relative ${
          isQuizPage && (!hasStartedQuiz || isSummeryScreen)
            ? 'mb-12'
            : `px-4 md:px-10 ${isActivity ? 'pb-11 md:pb-4' : isShowGrade ? 'md:pb-0' : 'pb-11 md:pb-4'}`
        } bg-white rounded-b`}
      >
        <div
          className={`flex flex-col ${
            isQuizPage
              ? hasStartedQuiz
                ? 'min-h-132 pb-5 justify-end'
                : 'min-h-sm'
              : isShowGrade && !isActivity
              ? ''
              : 'md:min-h-sm'
          }`}
        >
          <div className={showBossAnimation ? 'hidden' : 'contents'}>
            <ContentSection />
          </div>
          {isQuizPage && (
            <>
              <div className="absolute left-0 right-0 top-0">
                <QuizBoss
                  bossBattleEnemy={bossBattleEnemy}
                  setShowSplashScreen={setShowSplashScreen}
                  showOnModal
                  vccTitle={title || ''}
                />
              </div>
              <div className={`relative flex justify-between bottom-0 h-9 ${isSummeryScreen ? '-mt-16' : 'mt-4'}`}>
                <PlayerHealth
                  className={`transition duration-300 -mt-2 z-40 ${!isSummeryScreen ? 'opacity-100' : 'opacity-0'}`}
                  quizState={quizState}
                />
                <BossSoundControl
                  className={`self-end ml-auto z-40 ${showSplashScreen || (isSummeryScreen && 'px-4')} ${
                    !hasStartedQuiz ? 'p-4' : ''
                  }`}
                  color={showSplashScreen || isSummeryScreen || showBossAnimation ? 'text-white' : colorInfo.textClass}
                />
              </div>
            </>
          )}

          {isQuizPage && showSplashScreen && (
            <BossSplashScreen
              bossBattleEnemy={bossBattleEnemy}
              colorInfo={colorInfo}
              isQuizPage={isQuizPage}
              subjectName={subjectName || ''}
              title={title || ''}
            />
          )}
        </div>

        <ModalChestsCounter />
      </article>
      {showFunFactChest && !isMobile ? funFactChest : null}
    </Fragment>
  )
}

export default BootcampContent
